'use client'

import { INLink } from '@components/INLink'
import {
    BasicRegistrationFormErrorResponse,
    BasicRegistrationFormErrors,
    ErrorRegistrationFormValues,
    transformDataToSubmit,
    validationSchema,
} from '@util/registration/BasicFormModel'
import FirstScreen from '@app/registration/_components/basic/FirstScreen'
import SecondScreen from '@app/registration/_components/basic/SecondScreen'
import { BasicFormValues, initialFormValues } from '@app/registration/_components/basic/initialFormValues'
import { toFormErrorResponse } from '@data/api/mappers/toFormErrorResponse'
import { useBasicRegistrationMutation } from '@data/hooks/registration/useBasicRegistrationMutation'
import { useRegistrationWorkflow } from '@data/hooks/registration/useWorkflow'
import { Box, Button, ButtonSet, Typography } from '@internations/ds'
import { contact, login, registration } from '@routes/catalog'
import { trackEvent as trackGAEvent } from '@tracking/gtag'
import { Form, Formik, useFormikContext } from '@util/Formik'
import { whenContainerApp } from '@util/device'
import alreadyAMemberLabel from '@util/labels/alreadyAMember'
import joinNowLabel from '@util/labels/joinNow'
import loginNowLabel from '@util/labels/loginNow'
import nextLabel from '@util/labels/next'
import locationSelectorErrorLabel from '@util/labels/startTypingAndSelectACity'
import submitLabel from '@util/labels/submit'
import { getItem, removeItem, setItem } from '@util/localStorage'
import { FormikErrors, FormikTouched } from 'formik'
import { omit } from 'lodash-es'
import { ReactElement, useState } from 'react'
import { ValidationError } from 'yup'
import { useINAppRouter } from '@routes/utils/useINAppRouter'
import RegistrationPageSkeleton from '@app/registration/_components/registrationPageSkeleton'

export type BasicFormStepType = 'FirstStep' | 'SecondStep'
const FORM_DATA_STORAGE_KEY = 'registrationFormTempData'

type RegistrationFormProps = {
    isSubmitting: boolean
    currentStep: BasicFormStepType
    backendErrors?: BasicRegistrationFormErrors
    inviter?: TMember
    isEmailReadOnly?: boolean
    setBackendErrors: (errors: BasicRegistrationFormErrors) => void
}

function RegistrationForm({
    isSubmitting,
    backendErrors,
    setBackendErrors,
    inviter,
    isEmailReadOnly,
    currentStep,
}: RegistrationFormProps): ReactElement {
    const isSecondStep = currentStep === 'SecondStep'
    const buttonText = isSecondStep ? submitLabel : nextLabel
    const context = useFormikContext()

    const STEP_TO_SCREEN = {
        FirstStep: <FirstScreen inviter={inviter} backendErrors={backendErrors} isEmailReadOnly={isEmailReadOnly} />,
        SecondStep: <SecondScreen backendErrors={backendErrors} />,
    }

    return (
        <Form id="BasicRegistrationForm">
            {STEP_TO_SCREEN[currentStep]}
            <Box mt={3}>
                <ButtonSet>
                    <Button
                        className="t-next-step"
                        disabled={isSubmitting}
                        type="submit"
                        data-testid={buttonText.toLowerCase()}
                        onClick={() => {
                            const bornIn = context.getFieldMeta('bornIn')
                            const livingIn = context.getFieldMeta('livingIn')

                            if (currentStep === 'FirstStep') {
                                setBackendErrors({ ...backendErrors, bornIn: undefined, livingIn: undefined })
                            }
                            if (currentStep === 'SecondStep' && !(bornIn.value && livingIn.value)) {
                                const error = {
                                    bornIn: bornIn.value ? undefined : locationSelectorErrorLabel,
                                    livingIn: livingIn.value ? undefined : locationSelectorErrorLabel,
                                }
                                setBackendErrors({ ...backendErrors, ...error })
                            }
                            trackGAEvent({
                                eventName: 'button_clicked',
                                eventParams: {
                                    event_category: 'registration',
                                    event_label: isSecondStep ? 'submit_rs06b' : 'next_rs06b',
                                },
                            })
                        }}
                    >
                        {buttonText}
                    </Button>
                </ButtonSet>
                {currentStep === 'FirstStep'
                    ? whenContainerApp(() => (
                          <Typography variant="caption" mt={3} textAlign="center">
                              {alreadyAMemberLabel} <INLink route={login}>{loginNowLabel}</INLink>
                          </Typography>
                      ))
                    : null}
            </Box>
        </Form>
    )
}

export function transformBackendErrors(
    errorData: TFormErrorResponse<BasicRegistrationFormErrorResponse, ErrorRegistrationFormValues>
): BasicRegistrationFormErrors {
    const { namedErrors: backendErrors } = errorData
    const errors: BasicRegistrationFormErrors & ErrorRegistrationFormValues = {
        ...backendErrors,
    }
    const emailAlreadyTakenCode = 'EMAIL_ALREADY_TAKEN'
    const registrationDeclinedCode = 'REGISTRATION_DECLINED'

    if (backendErrors.email === emailAlreadyTakenCode) {
        errors.email = (
            <span>
                {`This Email address has already been registered. Please contact our `}
                <INLink route={contact}>Community Support Team</INLink>.
            </span>
        )
    }

    if (backendErrors.email === registrationDeclinedCode) {
        errors.email = (
            <span>
                {`Your membership has been declined. For further information, please get in touch with our `}
                <INLink route={contact}>Community Support Team</INLink>.
            </span>
        )
    }

    if (backendErrors['password.password']) {
        errors.password = backendErrors['password.password']
        delete errors['password.password']
    }

    return errors
}

const useInitialData = ({
    currentStep,
    inviteeFirstName,
    inviteeLastName,
    inviteeEmail,
}: {
    currentStep: BasicFormStepType
    inviteeFirstName?: string
    inviteeLastName?: string
    inviteeEmail?: string
}): {
    initialValues: BasicFormValues
    initialErrors: FormikErrors<BasicFormValues>
    initialTouched: FormikTouched<BasicFormValues>
} => {
    const storedValues = getItem(FORM_DATA_STORAGE_KEY, true) as BasicFormValues
    const initialValues: BasicFormValues = {
        ...initialFormValues,
        firstName: inviteeFirstName ?? initialFormValues.firstName,
        lastName: inviteeLastName ?? initialFormValues.lastName,
        email: inviteeEmail ?? initialFormValues.email,
        ...storedValues,
    }
    const initialErrors: FormikErrors<BasicFormValues> = {}
    const initialTouched: FormikTouched<BasicFormValues> = {}

    if (currentStep === 'FirstStep' && initialValues.email) {
        try {
            validationSchema.FirstStep.validateSyncAt('email', initialValues)
        } catch (e) {
            const error = e as ValidationError
            initialErrors.email = error.message
            initialTouched.email = true
        }
    }

    return { initialValues, initialErrors, initialTouched }
}

function RegistrationScreen({ step }: { step: 'second' | 'first' }): ReactElement | null {
    const { data: workflowData, isPending, navigateWorkflow } = useRegistrationWorkflow('basic')

    // FIXME we should not need to cast this type
    const data = workflowData as TBasicStepWorkflowData | undefined
    const currentStep = step === 'second' ? 'SecondStep' : 'FirstStep'
    const { go } = useINAppRouter()
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [backendErrors, setBackendErrors] = useState<BasicRegistrationFormErrors | undefined>(undefined)

    const { mutate: postBasicRegistration } = useBasicRegistrationMutation({
        onSuccess: (data: TBasicStepWorkflowData) => {
            navigateWorkflow(data?.workflow)
        },
        onError: (error) => {
            const statusCode = error.response?.status

            const BACKEND_FORM_VALIDATION_FAILED = 406
            if (statusCode === BACKEND_FORM_VALIDATION_FAILED) {
                const errorData = toFormErrorResponse<BasicRegistrationFormErrorResponse>(error.response?.data?.form)
                const errors = transformBackendErrors(errorData)
                setBackendErrors(errors)

                const firstStepFieldNames = ['firstName', 'lastName', 'email', 'password']

                // if any of the errors are from the first step, we send the user back to the first step
                if (Object.keys(errors).some((errorKey) => firstStepFieldNames.includes(errorKey))) {
                    go(
                        registration.params({
                            ...Object.fromEntries(new URLSearchParams(window.location.search).entries()),
                            step: 'first',
                        })
                    )
                }
            }
            setIsSubmitting(false)
        },
    })

    const onSubmit = async (values: BasicFormValues) => {
        if (currentStep === 'FirstStep') {
            const tempFormData = omit(values, ['password', 'repeatPassword'])

            setItem(FORM_DATA_STORAGE_KEY, tempFormData, true)
            go(
                registration.params({
                    ...Object.fromEntries(new URLSearchParams(window.location.search).entries()),
                    step: 'second',
                })
            )
        }

        if (currentStep === 'SecondStep') {
            setIsSubmitting(true)
            const dataToSubmit = transformDataToSubmit(values)
            postBasicRegistration(dataToSubmit)
            removeItem(FORM_DATA_STORAGE_KEY, true)
        }
    }

    const { initialValues, initialErrors, initialTouched } = useInitialData({
        currentStep,
        inviteeFirstName: data?.invitee?.firstName,
        inviteeLastName: data?.invitee?.lastName,
        inviteeEmail: data?.invitee?.email,
    })

    if (isPending) {
        return <RegistrationPageSkeleton />
    }

    if (!data) {
        return null
    }

    return (
        <>
            <title>{joinNowLabel}</title>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema[currentStep]}
                initialErrors={initialErrors}
                initialTouched={initialTouched}
                onSubmit={onSubmit}
            >
                {() => (
                    <RegistrationForm
                        isSubmitting={isSubmitting}
                        currentStep={currentStep}
                        inviter={data?.inviter}
                        isEmailReadOnly={data?.isEmailReadOnly}
                        backendErrors={backendErrors}
                        setBackendErrors={setBackendErrors}
                    />
                )}
            </Formik>
        </>
    )
}

export default RegistrationScreen
